import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import {
  Paciente,
  PatientBindTagPayload,
  PatientCreatePayload,
  PatientFilterParams,
  PatientPatchPayload,
  PatientRegisterPayload,
} from 'src/app/models/paciente.model';
import { captureError, logErrorAction, logInfoAction } from 'src/app/shared/util/error-capture.util';
import { environment } from 'src/environments/environment';
import { TagList } from '../models/configuration.model';
import { ImportService } from '../shared/interfaces/import.interface';

@Injectable({
  providedIn: 'root',
})
export class PatientService implements ImportService {
  API = `${environment.backendUrl}paciente`;

  private haveDatePasswordSubject = new Subject<boolean>();
  haveDatePassword$: Observable<boolean> = this.haveDatePasswordSubject.asObservable();

  constructor(private http: HttpClient, private authService: AuthService) { }

  updateDatePassword(haveDatePassword: boolean) {
    this.haveDatePasswordSubject.next(haveDatePassword);
  }


  async import(file): Promise<Response> {
    try {
      const headers = await this.authService.buildAPIAuthHeader(file.type);
      const uploadedFile = new FormData();
      uploadedFile.append('file', file);
      logInfoAction('Patient import started', { fileName: file?.name, fileType: file?.type });
      return await this.http
        .post<Response>(this.API + '/importar', uploadedFile, { headers })
        .toPromise();
    } catch (error) {
      logErrorAction('Patient import failed', error);
      captureError(error, {
        component: 'patient-service',
        operation: 'import',
        extraData: { fileName: file?.name, fileType: file?.type }
      });
      throw error;
    }
  }

  async loadPatientList(
    queryParams?: PatientFilterParams
  ): Promise<{ count: number; patients: Array<Paciente> }> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      const options = { headers, params: {} };

      if (queryParams) {
        let params = new HttpParams();

        if (queryParams.filterName != null && queryParams.filterName.length) {
          params = params.set('filterName', queryParams.filterName);
        }

        if (queryParams.filterTags != null && queryParams.filterTags.length) {
          params = params.set('filterEtiquetas', queryParams.filterTags);
        }

        if (queryParams.page) {
          params = params.set('page', queryParams.page);
        }

        if (queryParams.pageSize) {
          params = params.set('pageSize', queryParams.pageSize + 1);
        }

        if (queryParams.sortBy?.length) {
          params = params.set('sortBy', queryParams.sortBy);
        }

        if (queryParams.sortType?.length) {
          params = params.set('sortType', queryParams.sortType);
        }

        options.params = params;
      }

      logInfoAction('Patient list loading started', { queryParams });
      return this.http
        .get<{ count: number; patients: Array<Paciente> }>(this.API, options)
        .toPromise();
    } catch (error) {
      logErrorAction('Patient list loading failed', error);
      captureError(error, {
        component: 'patient-service',
        operation: 'loadPatientList',
        extraData: { queryParams }
      });
      throw error;
    }
  }

  async loadPatient(id: string): Promise<any> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      logInfoAction('Patient loading started', { id });
      return this.http
        .get<Paciente>(`${this.API}/${id}`, { headers })
        .toPromise();
    } catch (error) {
      logErrorAction('Patient loading failed', error);
      captureError(error, {
        component: 'patient-service',
        operation: 'loadPatient',
        extraData: { patientId: id }
      });
      throw error;
    }
  }

  async getPatientInfo(): Promise<Paciente> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      logInfoAction('Patient info fetch started', { userId: this.authService.getUserUid() });
      return this.http
        .get<Paciente>(this.API + `/${this.authService.getUserUid()}`, {
          headers,
        })
        .toPromise();
    } catch (error: any) {
      logErrorAction('Getting patient info failed', error);
      captureError(error, {
        component: 'patient-service',
        operation: 'getPatientInfo',
        extraData: { userId: this.authService.getUserUid() }
      });
      throw error;
    }
  }

  async patchPatient(id: number, payload: PatientPatchPayload): Promise<void> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      logInfoAction('Patient update started', { id, payload });
      await this.http
        .patch(this.API + `/${id}`, payload, { headers })
        .toPromise();
    } catch (error) {
      logErrorAction('Patching patient failed', error);
      captureError(error, {
        component: 'patient-service',
        operation: 'patchPatient',
        extraData: { patientId: id, payload }
      });
      throw error;
    }
  }

  async addRemoveTag(patientId: number, payload: TagList): Promise<void> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      logInfoAction('Patient tags update started', { patientId, payload });
      await this.http
        .patch(this.API + `/${patientId}/etiquetas`, payload, { headers })
        .toPromise();
    } catch (error) {
      logErrorAction('Adding/Removing patient tag failed', error);
      captureError(error, {
        component: 'patient-service',
        operation: 'addRemoveTag',
        extraData: { patientId, payload }
      });
      throw error;
    }
  }

  async bindTagToPatients(payload: PatientBindTagPayload): Promise<void> {
    try {
      const headers = await this.authService.buildAPIAuthHeader();
      logInfoAction('Patient bulk tag bind started', { payload });
      await this.http
        .post(`${environment.backendUrl}etiqueta/bind-pacientes`, payload, { headers })
        .toPromise();
    } catch (error) {
      logErrorAction('Patient bulk tag bind failed', error);
      captureError(error, {
        component: 'patient-service',
        operation: 'bindTagToPatients',
        extraData: { payload }
      });
      throw error;
    }
  }

  async registerPatient(
    payload: PatientCreatePayload | PatientRegisterPayload
  ): Promise<any> {
    try {
      logInfoAction('Patient registration started', { payload });
      const headers = await this.authService.buildAPIAuthHeader();
      const response = await this.http.post(this.API, payload, { headers }).toPromise();
      return response;
    } catch (error) {
      captureError(error, {
        component: 'patient-service',
        operation: 'registerPatient',
        extraData: { payload }
      });
      logErrorAction('Patient registration failed', error);
      throw error;
    }
  }
}
